import React from 'react';

export type ChatLinkTone = 'announcement' | 'system' | 'local' | 'remote';

const LINK_CLASS_BY_TONE: Record<ChatLinkTone, string> = {
  announcement: 'underline text-yellow-200 hover:text-yellow-100 break-words',
  system: 'underline text-red-200 hover:text-red-100 break-words',
  local: 'underline text-blue-100 hover:text-white break-words',
  remote: 'underline text-sky-300 hover:text-sky-200 break-words',
};

function normalizeToSafeHref(raw: string): string | null {
  const trimmed = raw.replace(/[.,;:!?]+$/u, '');
  const withScheme = trimmed.startsWith('www.') ? `https://${trimmed}` : trimmed;
  try {
    const u = new URL(withScheme);
    if (u.protocol !== 'http:' && u.protocol !== 'https:') return null;
    return u.href;
  } catch {
    return null;
  }
}

/**
 * Renders plain text with http(s) and www. URLs as external links. No HTML injection.
 */
export function linkifyChatMessage(text: string, tone: ChatLinkTone): React.ReactNode {
  if (!text) return null;

  const urlPattern = /(https?:\/\/[^\s]+|www\.[^\s]+)/gi;
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let key = 0;

  while ((match = urlPattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }

    const raw = match[0];
    const trimmed = raw.replace(/[.,;:!?]+$/u, '');
    const tail = raw.slice(trimmed.length);
    const href = normalizeToSafeHref(trimmed);
    const linkClass = LINK_CLASS_BY_TONE[tone];

    if (href) {
      parts.push(
        <a
          key={`link-${key++}`}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className={linkClass}
          onClick={(e) => e.stopPropagation()}
        >
          {trimmed}
        </a>
      );
      if (tail) parts.push(tail);
    } else {
      parts.push(raw);
    }

    lastIndex = match.index + raw.length;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return <>{parts}</>;
}
